import {
  createSlice,
  createAsyncThunk,
  createSelector,
} from "@reduxjs/toolkit";

import type { RootState } from "../../../store/store";
import { securityAlertsService } from "../services/securityAlertsService";
import { configService } from "@/features/admin/services/configService";
import type { SecurityAlertState } from "../types/securityAlerts.types";

const initialState: SecurityAlertState = {
  SecurityAlertsDst: [],
  SubscriptionsDst: [],
  ServicesDst: [],
  status: {
    SecurityAlertsDst: "idle",
    SubscriptionsDst: "idle",
    StatusDst: "idle",
    ServicesDst: "idle",
  },
  filters: {
    subscriptions: [],
    services: [],
    status: ["Active", "In progress"],
    securityType: "Alerts",
  },
  filtersInitialised: true,
};

export const fetchMasterSecurityAlerts = createAsyncThunk(
  "data/fetchMasterSecurityAlerts",
  async (TimeZone: string) => {
    return await securityAlertsService.GetAllSecurityAlerts(TimeZone);
  },
);

export const fetchSubscriptions = createAsyncThunk(
  "data/fetchSubscriptions",
  async () => {
    const res = await configService.GetSubsDetails();
    return res.map((i) => i.subscriptionShortName!);
  },
);

export const fetchServices = createAsyncThunk(
  "data/fetchServices",
  async () => {
    return await securityAlertsService.GetServices();
  },
);

const securityAlertsSlice = createSlice({
  name: "alert",
  initialState,
  reducers: {
    setFilters(state, action) {
      state.filters = { ...state.filters, ...action.payload };
    },
    clearFilters(state) {
      state.filters = {
        subscriptions: [],
        services: [],
        status: ["Active", "In progress"],
        securityType: "Alerts",
      };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchMasterSecurityAlerts.fulfilled, (state, action) => {
        state.SecurityAlertsDst = action.payload;
        state.status.SecurityAlertsDst = "succeeded";
      })
      .addCase(fetchMasterSecurityAlerts.rejected, (state) => {
        state.status.SecurityAlertsDst = "failed";
      })
      .addCase(fetchSubscriptions.fulfilled, (state, action) => {
        state.SubscriptionsDst = action.payload;
        state.status.SubscriptionsDst = "succeeded";
      })
      .addCase(fetchSubscriptions.rejected, (state) => {
        state.status.SubscriptionsDst = "failed";
      })
      .addCase(fetchServices.fulfilled, (state, action) => {
        state.ServicesDst = action.payload;
        state.status.ServicesDst = "succeeded";
      })
      .addCase(fetchServices.rejected, (state) => {
        state.status.ServicesDst = "failed";
      });
  },
});

// --- Selectors ---
export const selectFilters = (state: RootState) =>
  state.securityAlerts.filters;

export const selectSecurityRaw = (state: RootState) =>
  state.securityAlerts.SecurityAlertsDst;

// --- Security Alerts Filtering ---
export const selectFilteredSecurityAlerts = createSelector(
  [selectSecurityRaw, selectFilters],
  (alerts, filters) => {
    let filtered = alerts;

    if (filters.subscriptions.length > 0) {
      filtered = filtered.filter((a) =>
        filters.subscriptions.includes(a.subscriptionShortName ?? ""),
      );
    }
    if (filters.services.length > 0) {
      filtered = filtered.filter((a) =>
        filters.services.includes(a.alerttype ?? ""),
      );
    }
    if (filters.status.length > 0) {
      filtered = filtered.filter((a) =>
        filters.status.includes(a.status ?? ""),
      );
    }

    if (filters.securityType === "Alerts") {
      filtered = filtered.filter(
        (a) => a.isincident?.toLowerCase() === "false",
      );
    } else if (filters.securityType === "Incidents") {
      filtered = filtered.filter((a) => a.isincident?.toLowerCase() === "true");
    }

    return filtered;
  },
);

export const { setFilters, clearFilters } = securityAlertsSlice.actions;
export default securityAlertsSlice.reducer;
